import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, AlertCircle, Camera, Image as ImageIcon, X, Loader2, Phone, Mail, MessageSquare } from 'lucide-react';
import { parseTradeInfo, ParsedTradeInfo } from './parseTradeInfo';
import { insertTradesperson } from '@/lib/supabase/trades';
import { addUserTradesperson, searchTradeByContact, isTradesPersonSavedByUser } from '@/lib/supabase/trades';
import { Tradesperson } from '@/lib/types';
import { debug } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { performOCR } from './imageUtils';
import { getTradeIconForTrade } from '@/lib/tradeUtils';
import { TradePersonCard } from '@/tradespurple/components/TradePersonCard';
import { TRADE_CATEGORIES } from '@/lib/tradeCategories';

interface QuickAddTradeProps {
  onSuccess?: () => void;
  userPostcode?: string;
  onInputChange?: (isActive: boolean) => void;
}

export function QuickAddTrade({ onSuccess, userPostcode, onInputChange }: QuickAddTradeProps) {
  const { user } = useAuth();
  const userId = user?.id;

  // Input state
  const [inputText, setInputText] = useState('');
  const debouncedInputText = useDebounce(inputText, 500); // 500ms debounce

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Progressive disclosure state
  const [isExpanded, setIsExpanded] = useState(false);

  // Display mode state - controls what UI is shown
  type DisplayMode = 'form' | 'alreadySaved' | 'existingRecord';
  const [displayMode, setDisplayMode] = useState<DisplayMode>('form');

  // Parsing state
  const [isParsing, setIsParsing] = useState(false);
  const [parsedInfo, setParsedInfo] = useState<ParsedTradeInfo>({
    name: '',
    trade_type: '',
    phone: '',
    email: '',
    postcode: userPostcode || '',
    detectedFields: []
  });

  // Photo state
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);

  // Editable fields (what the user can modify)
  const [editableFields, setEditableFields] = useState({
    name: '',
    trade_type: '',
    phone: '',
    email: '',
    postcode: userPostcode || '',
    notes: '', // Adding notes field
  });

  // Duplicate detection
  const [possibleMatch, setPossibleMatch] = useState<Tradesperson | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExistingRecord, setIsExistingRecord] = useState(false);
  const [isAlreadySavedByUser, setIsAlreadySavedByUser] = useState(false);

  // Define option type with optional disabled property
  interface TradeOption {
    value: string;
    label: string;
    disabled?: boolean;
  }

  // Group trade categories by UI category for the dropdown
  const getTradeOptions = (): TradeOption[] => {
    // Get unique UI categories
    const uiCategories = Array.from(
      new Set(
        TRADE_CATEGORIES
          .filter(category => !category.isLegacy)
          .map(category => category.uiCategory)
      )
    ).sort();

    // Start with empty option
    const options: TradeOption[] = [{ value: '', label: 'Select trade type...' }];

    // Add trades grouped by UI category
    uiCategories.forEach(uiCategory => {
      if (!uiCategory) return;

      // Get trades for this UI category
      const categoryTrades = TRADE_CATEGORIES
        .filter(trade => trade.uiCategory === uiCategory && !trade.isLegacy)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      // Add option group label
      options.push({
        value: `group-${uiCategory}`,
        label: `--- ${uiCategory.charAt(0).toUpperCase() + uiCategory.slice(1)} ---`,
        disabled: true
      });

      // Add trades in this category
      categoryTrades.forEach(trade => {
        options.push({
          value: trade.id,
          label: trade.displayName
        });
      });
    });

    return options;
  };

  // Available trade types for the dropdown
  const tradeOptions = getTradeOptions();

  // Handle photo capturing and processing
  const handleCapturePhoto = async (file: File) => {
    try {
      // Validate file is an image
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Set the photo and create preview URL immediately
      // This ensures the UI shows the photo even if OCR fails
      setPhoto(file);
      setPhotoPreviewUrl(URL.createObjectURL(file));
      setIsProcessingImage(true);
      setIsExpanded(true); // Always expand to allow manual entry
      onInputChange?.(true);

      // Initialize the form fields so the user can start entering data
      // even before OCR completes
      setEditableFields(prev => ({
        ...prev,
        name: prev.name,
        trade_type: prev.trade_type,
        phone: prev.phone,
        email: prev.email,
        postcode: userPostcode || prev.postcode || '',
        notes: prev.notes,
      }));

      debug.log('Starting OCR processing for file:', file.name, file.type, file.size);

      // Process the image with OCR in the background
      try {
        const extractedText = await performOCR(file);
        debug.log('Extracted text from image:', extractedText);

        if (!extractedText || extractedText.trim().length === 0) {
          toast.warning('No text was detected in the image. Try a clearer photo or enter details manually.');
        } else {
          // Set the extracted text in the input field
          setInputText(extractedText);

          // Make sure form stays expanded after text is set
          setIsExpanded(true);
          onInputChange?.(true);
        }
      } catch (ocrError) {
        debug.error('OCR processing error:', ocrError);

        // More specific error messages based on error type
        if (ocrError.message && ocrError.message.includes('timed out')) {
          toast.error('Image processing timed out. Please enter details manually.');
        } else if (ocrError.message && ocrError.message.includes('text recognition engine')) {
          toast.error('Could not load text recognition engine. Please try again later.');
        } else {
          toast.error('Could not process image. Try using a clearer photo or manually enter the details.');
        }

        // Continue with empty text so the user can enter information manually
        toast.info('You can still enter the tradesperson details manually below.');
      }

      setIsProcessingImage(false);
    } catch (error) {
      debug.error('Error handling photo capture:', error);
      toast.error('Failed to process image. Please try again or enter details manually.');
      setIsProcessingImage(false);

      // Ensure UI remains in a usable state even after errors
      setIsExpanded(true);
      onInputChange?.(true);
    }
  };

  // Remove the photo and reset related state
  const handleRemovePhoto = () => {
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhoto(null);
    setPhotoPreviewUrl(null);
  };

  // Parse the input text whenever it changes (debounced)
  useEffect(() => {
    const parseInput = async () => {
      if (!debouncedInputText) {
        // Clear parsed info if input is empty
        setParsedInfo({
          name: '',
          trade_type: '',
          phone: '',
          email: '',
          postcode: userPostcode || '',
          detectedFields: []
        });

        // Reset duplicate detection state
        setPossibleMatch(null);
        setIsExistingRecord(false);
        setIsAlreadySavedByUser(false);
        setDisplayMode('form');

        // Reset form fields to blank state
        setEditableFields({
          name: '',
          trade_type: '',
          phone: '',
          email: '',
          postcode: userPostcode || '',
          notes: '',
        });

        // Collapse the component
        setIsExpanded(false);
        onInputChange?.(false);
        return;
      }

      // If user has started typing, expand the component
      setIsExpanded(true);
      onInputChange?.(true);
      setIsParsing(true);

      try {
        // Parse the text
        const parsed = parseTradeInfo(debouncedInputText);
        setParsedInfo(parsed);

        // Update editable fields with parsed values, but don't override user-edited values
        // Always keep the user's postcode, don't use detected postcodes
        setEditableFields(prev => ({
          name: parsed.name || prev.name,
          trade_type: parsed.trade_type || prev.trade_type,
          phone: parsed.phone || prev.phone,
          email: parsed.email || prev.email,
          postcode: userPostcode || prev.postcode || '',
          notes: prev.notes, // Preserve any existing notes
        }));

        // Check for duplicates if we have a phone or email
        if (parsed.phone || parsed.email) {
          debug.log("QuickAddTrade: Checking for duplicate tradesperson:", {
            phone: parsed.phone,
            email: parsed.email
          });

          const duplicate = await searchTradeByContact(parsed.phone, parsed.email);

          debug.log("QuickAddTrade: Search result:", duplicate);

          // If we find a match, fill the form with the existing data
          if (duplicate) {
            debug.log("QuickAddTrade: Match found! Showing existing tradesperson:", duplicate);

            // Store the match and mark as existing record
            setPossibleMatch(duplicate);
            setIsExistingRecord(true);

            // Check if this tradesperson is already saved by the user
            if (userId) {
              try {
                const isSaved = await isTradesPersonSavedByUser(userId, duplicate.id);
                debug.log("QuickAddTrade: Is tradesperson already saved by user:", isSaved);
                setIsAlreadySavedByUser(isSaved);

                // Update the display mode based on whether it's already saved or just in the database
                if (isSaved) {
                  setDisplayMode('alreadySaved');
                } else {
                  setDisplayMode('existingRecord');
                }
              } catch (error) {
                debug.error("QuickAddTrade: Error checking if tradesperson is saved:", error);
                setIsAlreadySavedByUser(false);
                setDisplayMode('existingRecord');
              }
            } else {
              // If no user ID, just show existingRecord view
              setDisplayMode('existingRecord');
            }

            // Pre-fill form with the matched tradesperson's data
            // Get current notes to preserve them
            const currentNotes = editableFields.notes;

            // Debug any nulls in the duplicate data
            if (!duplicate.org && !duplicate.contact) {
              debug.warn("QuickAddTrade: Duplicate has no name data (org or contact)");
            }

            if (!duplicate.trade_type && !duplicate.category) {
              debug.warn("QuickAddTrade: Duplicate has no trade type data");
            }

            // Pre-fill form with the matched tradesperson's data - explicitly force values
            const existingData = {
              name: duplicate.org || duplicate.contact || "",
              trade_type: duplicate.trade_type || duplicate.category || "",
              phone: duplicate.phone || "",
              email: duplicate.email || "",
              postcode: userPostcode || "",
              notes: currentNotes // Preserve any existing notes
            };

            debug.log("QuickAddTrade: Pre-filling form with:", existingData);

            // Update form fields with the existing data
            setEditableFields(existingData);
          } else {
            debug.log("QuickAddTrade: No match found");
            setPossibleMatch(null);
            setIsExistingRecord(false);
            setIsAlreadySavedByUser(false);
            setDisplayMode('form');
          }
        }
      } catch (error) {
        debug.error('Error parsing trade info:', error);
      } finally {
        setIsParsing(false);
      }
    };

    parseInput();
  }, [debouncedInputText, userPostcode, onInputChange, userId]);

  // Update a single editable field
  const updateField = (field: keyof typeof editableFields, value: string) => {
    setEditableFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Use existing tradesperson
  const handleUseExisting = async () => {
    if (!possibleMatch || !userId) return;

    setIsSubmitting(true);

    try {
      await addUserTradesperson(userId, possibleMatch.id, {
        preferred: false,
        notes: editableFields.notes ?
          editableFields.notes :
          `Added via quick entry on ${new Date().toLocaleDateString()}`
      });

      toast.success('Tradesperson added to your list!');
      resetForm();
      onSuccess?.();
    } catch (error) {
      debug.error('Error adding existing tradesperson:', error);
      toast.error('Failed to add tradesperson to your list');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Continue with new tradesperson - preserving phone/email
  const handleContinueWithNew = () => {
    // Store current values we want to keep
    const currentPhone = editableFields.phone;
    const currentEmail = editableFields.email;

    setPossibleMatch(null);
    setIsExistingRecord(false);
    setDisplayMode('form');

    // Clear form fields but keep the detected phone/email that triggered the match
    setEditableFields({
      name: '',
      trade_type: '',
      phone: currentPhone, // Keep current phone number
      email: currentEmail, // Keep current email
      postcode: userPostcode || '',
      notes: ''
    });

    debug.log("User chose to create new record - cleared form but kept contact info:", {
      phone: currentPhone,
      email: currentEmail
    });
  };

  // Save new tradesperson
  const handleSave = async () => {
    if (!userId) {
      toast.error('You must be logged in to add tradespeople');
      return;
    }

    // Validate minimum required fields
    if (!editableFields.name) {
      toast.error('Please provide a name for the tradesperson');
      return;
    }

    if (!editableFields.trade_type) {
      toast.error('Please select a trade type');
      return;
    }

    setIsSubmitting(true);

    try {
      // Insert new tradesperson
      const result = await insertTradesperson({
        contact: editableFields.name,
        org: editableFields.name, // Use name for organization as well for MVP
        category: editableFields.trade_type,
        trade_type: editableFields.trade_type,
        phone: editableFields.phone,
        email: editableFields.email,
        postcode: userPostcode || '', // Always use the user's postcode
        verified: false,
        created_by: userId,
      });

      if (!result.data || result.data.length === 0) {
        throw new Error('Failed to create tradesperson');
      }

      // Add to user's list
      await addUserTradesperson(userId, result.data[0].id, {
        preferred: false,
        notes: editableFields.notes ?
          editableFields.notes :
          `Added via quick entry on ${new Date().toLocaleDateString()}`
      });

      toast.success('Tradesperson added successfully!');
      resetForm();
      onSuccess?.();
    } catch (error) {
      debug.error('Error saving tradesperson:', error);
      toast.error('Failed to save tradesperson');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle the "Start Over" action
  const handleStartOver = () => {
    resetForm();
  };

  // Reset the form
  const resetForm = () => {
    setInputText('');
    setParsedInfo({
      name: '',
      trade_type: '',
      phone: '',
      email: '',
      postcode: userPostcode || '',
      detectedFields: []
    });
    setEditableFields({
      name: '',
      trade_type: '',
      phone: '',
      email: '',
      postcode: userPostcode || '', // Keep this in the state for data consistency
      notes: '', // Clear notes field
    });
    setPossibleMatch(null);
    setIsExistingRecord(false);
    setIsAlreadySavedByUser(false);
    setDisplayMode('form');

    // Clear photo state
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhoto(null);
    setPhotoPreviewUrl(null);
  };

  // Render the already saved comparison view
  const renderAlreadySavedView = () => {
    if (!possibleMatch) return null;

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-5 shadow-sm">
        <div className="flex justify-center items-center gap-2 mb-4 text-green-700">
          <Check className="h-5 w-5" />
          <h3 className="text-lg font-medium">This contact is already in your list!</h3>
        </div>

        {/* Photo preview if available */}
        {photoPreviewUrl && (
          <div className="mb-5 border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white">
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 font-medium border-b">
              Your Photo
            </div>
            <div className="p-2">
              <img
                src={photoPreviewUrl}
                alt="Business card"
                className="w-full max-h-[180px] object-contain"
              />
            </div>
          </div>
        )}

        {/* Input preview if no photo */}
        {!photoPreviewUrl && inputText && (
          <div className="mb-5 border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white">
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 font-medium border-b">
              Your Input
            </div>
            <div className="p-4 text-gray-800 font-mono text-sm whitespace-pre-wrap">
              {inputText}
            </div>
          </div>
        )}

        {/* Saved trade card */}
        <div className="mb-5 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 font-medium border-b">
            From Your List
          </div>
          <div className="p-2">
            {/* Import and use SavedTradeCard component */}
            <div className="bg-white">
              <TradePersonCard trade={possibleMatch} mode="saved" username={undefined} />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-4 mt-6">
          <Button
            onClick={() => {
              resetForm();
              onSuccess?.(); // This will typically navigate to the list view
            }}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg text-base font-medium shadow-sm"
          >
            Show in My List
          </Button>
          <Button
            onClick={handleStartOver}
            variant="outline"
            className="flex-1 border-gray-300 text-gray-700 py-3 rounded-lg text-base font-medium"
          >
            Start Over
          </Button>
        </div>
      </div>
    );
  };

  // Render the form view for adding tradespeople
  const renderFormView = () => {
    return (
      <>
        <div className="mb-4">
          <label htmlFor="trade-info" className="block text-sm font-medium text-purple-800 mb-2">
            Enter tradesperson details
          </label>

          {/* Photo Preview */}
          {photoPreviewUrl && (
            <div className="relative mb-4 border border-purple-200 rounded-lg overflow-hidden shadow-sm">
              <div className="absolute top-3 right-3 z-10">
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="bg-white rounded-full p-1.5 shadow-md hover:bg-red-50"
                  aria-label="Remove photo"
                >
                  <X className="h-5 w-5 text-red-500" />
                </button>
              </div>
              <img
                src={photoPreviewUrl}
                alt="Business card"
                className="w-full max-h-[200px] object-contain"
              />
            </div>
          )}

          <div className="relative">
            <textarea
              id="trade-info"
              value={inputText}
              onChange={(e) => {
                const newValue = e.target.value;
                setInputText(newValue);
                // Immediately call onInputChange to respond quickly to user input
                onInputChange?.(newValue.trim().length > 0);
              }}
              placeholder="Name, business, contact info..."
              className="w-full border-2 border-purple-200 rounded-lg p-4 mb-4 min-h-[100px] resize-none focus:border-purple-400 focus:outline-none text-base"
              disabled={isProcessingImage}
            />
          </div>

          {/* Unified image capture button */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 bg-purple-100 hover:bg-purple-200 text-purple-800 font-medium py-3 px-4 rounded-lg transition-colors"
              aria-label="Add photo"
              title="Add photo of business card or tradesperson"
              disabled={isProcessingImage}
            >
              <Camera className="h-5 w-5" />
              <span>Add Photo</span>
            </button>

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              // Removed capture="environment" attribute as it can cause issues on iOS
              className="hidden"
              data-testid="photo-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleCapturePhoto(file);
                }
                // Reset the input value to allow selecting the same file again
                e.target.value = '';
              }}
            />
          </div>

          {/* Processing states */}
          {isParsing && !isProcessingImage && (
            <div className="text-sm text-purple-600 mb-2">
              <span className="inline-block animate-pulse">Detecting information...</span>
            </div>
          )}

          {isProcessingImage && (
            <div className="text-sm text-purple-600 flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                <span>Processing image...</span>
              </div>
              {photoPreviewUrl && (
                <button
                  type="button"
                  onClick={() => setIsProcessingImage(false)}
                  className="ml-4 text-xs text-purple-700 underline"
                >
                  Skip processing
                </button>
              )}
            </div>
          )}
        </div>

        {/* Expanded form fields - only shown when user starts typing */}
        {isExpanded && (
          <>
            {/* Existing tradesperson notification */}
            {isExistingRecord && possibleMatch && displayMode === 'existingRecord' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-amber-100 p-1 rounded-full">
                    <Check className="h-5 w-5 text-amber-600" />
                  </div>
                  <p className="text-base text-amber-700 font-medium">
                    Hey! We found this tradesperson already in our database!
                  </p>
                </div>
                <p className="text-sm text-amber-600 mb-3">
                  Is this who you're looking for? You can use the existing record or create a new one.
                </p>
              </div>
            )}

            {/* Business Card Design */}
            <div className="bg-white border border-purple-300 rounded-xl p-5 shadow-md mb-5">
              {/* Card Header with Logo area */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  {editableFields.trade_type ? (
                    <div className="rounded-lg bg-purple-100 p-2 mr-3">
                      {(() => {
                        // Get the trade type icon
                        const tradeType = editableFields.trade_type;
                        const TradeIcon = getTradeIconForTrade({ trade_type: tradeType });
                        return <TradeIcon className="h-5 w-5 text-purple-700" />;
                      })()}
                    </div>
                  ) : (
                    <div className="rounded-lg bg-gray-100 p-2 mr-3">
                      <span className="h-5 w-5 text-gray-400 flex items-center justify-center text-xs">?</span>
                    </div>
                  )}

                  <div className="flex-1">
                    {/* Name field looks like business card title */}
                    <div className="relative w-full mb-1">
                      <input
                        id="trade-name"
                        type="text"
                        value={editableFields.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        className="w-full py-1 px-0 text-lg font-medium text-purple-900 border-0 border-b border-transparent hover:border-purple-200 focus:border-purple-400 focus:outline-none bg-transparent"
                        placeholder="Business Name"
                      />
                      {parsedInfo.detectedFields.includes('name') && (
                        <span className="absolute right-0 top-1 text-xs text-green-600 flex items-center">
                          <Check className="h-3 w-3 mr-1" /> Detected
                        </span>
                      )}
                    </div>

                    {/* Trade Type field looks like a subtitle */}
                    <div className="relative w-full">
                      <select
                        id="trade-type"
                        value={editableFields.trade_type}
                        onChange={(e) => updateField('trade_type', e.target.value)}
                        className="w-full py-1 px-0 text-sm text-purple-700 border-0 border-b border-transparent hover:border-purple-200 focus:border-purple-400 focus:outline-none appearance-none cursor-pointer bg-transparent"
                      >
                        {tradeOptions.map(option => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={option.value.startsWith('group-')}
                            className={option.value.startsWith('group-') ? 'font-bold bg-purple-50' : ''}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {parsedInfo.detectedFields.includes('trade_type') && (
                        <span className="absolute right-0 top-1 text-xs text-green-600 flex items-center">
                          <Check className="h-3 w-3 mr-1" /> Detected
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact information section */}
              <div className="mb-4 space-y-3">
                {/* Phone field */}
                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-purple-600 mr-3 flex-shrink-0" />
                  <div className="relative flex-1">
                    <input
                      id="trade-phone"
                      type="tel"
                      value={editableFields.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="Phone number"
                      className="w-full py-1 px-0 text-base text-gray-800 border-0 border-b border-transparent hover:border-purple-200 focus:border-purple-400 focus:outline-none bg-transparent"
                    />
                    {parsedInfo.detectedFields.includes('phone') && (
                      <span className="absolute right-0 top-1 text-xs text-green-600 flex items-center">
                        <Check className="h-3 w-3 mr-1" /> Detected
                      </span>
                    )}
                  </div>
                </div>

                {/* Email field */}
                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-purple-600 mr-3 flex-shrink-0" />
                  <div className="relative flex-1">
                    <input
                      id="trade-email"
                      type="email"
                      value={editableFields.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="Email address"
                      className="w-full py-1 px-0 text-base text-gray-800 border-0 border-b border-transparent hover:border-purple-200 focus:border-purple-400 focus:outline-none bg-transparent"
                    />
                    {parsedInfo.detectedFields.includes('email') && (
                      <span className="absolute right-0 top-1 text-xs text-green-600 flex items-center">
                        <Check className="h-3 w-3 mr-1" /> Detected
                      </span>
                    )}
                  </div>
                </div>

                {/* Notes field */}
                <div className="flex items-start pt-1">
                  <MessageSquare className="h-4 w-4 text-purple-600 mr-3 flex-shrink-0 mt-1" />
                  <div className="relative flex-1">
                    <textarea
                      id="trade-notes"
                      value={editableFields.notes || ''}
                      onChange={(e) => updateField('notes', e.target.value)}
                      placeholder="Add notes about this tradesperson..."
                      className="w-full py-1 px-0 text-base text-gray-800 border-0 border-b border-transparent hover:border-purple-200 focus:border-purple-400 focus:outline-none bg-transparent resize-none min-h-[40px]"
                    />
                  </div>
                </div>
              </div>

              {/* Postcode info - styled to look like part of the card */}
              {userPostcode && (
                <div className="flex items-center justify-end text-xs text-purple-700 mt-2">
                  <Check className="h-3 w-3 mr-1 text-purple-700" />
                  <span>Area: <strong>{userPostcode}</strong></span>
                </div>
              )}
            </div>

            <div className="mt-4">
              {displayMode === 'existingRecord' && possibleMatch ? (
                <div className="flex gap-3">
                  <Button
                    onClick={handleUseExisting}
                    disabled={isSubmitting}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-lg text-base font-medium shadow-sm"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding to My List...
                      </span>
                    ) : (
                      'Use Existing Record'
                    )}
                  </Button>
                  <Button
                    onClick={handleContinueWithNew}
                    disabled={isSubmitting}
                    variant="outline"
                    className="flex-1 border-amber-600 text-amber-700 py-3 rounded-lg text-base font-medium"
                  >
                    Create New
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleSave}
                  disabled={isSubmitting || !editableFields.name || !editableFields.trade_type}
                  className="w-full bg-purple-700 hover:bg-purple-800 text-white py-3 rounded-lg text-base font-medium shadow-sm"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding Tradesperson...
                    </span>
                  ) : (
                    'Add Tradesperson'
                  )}
                </Button>
              )}

              {(!editableFields.name || !editableFields.trade_type) && (
                <p className="text-sm text-center text-amber-600 mt-3 flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Name and trade type are required
                </p>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  // Render based on display mode
  return (
    <div className="p-0 mb-3 transition-all duration-300">
      <h3 className="text-xl font-bold text-purple-900 mb-4">Add a Tradesperson</h3>

      {displayMode === 'alreadySaved' ? renderAlreadySavedView() : renderFormView()}
    </div>
  );
}
